import React from "react";
import { motion } from "framer-motion";
import { useInView } from "hooks/useInView";

import {
  Shield,
  // MessageCircle, // Commented out for Facebook review
  BarChart3,
  Languages,
  Brain,
  Users,
} from "lucide-react";

const features = [
  {
    icon: Shield,
    title: "Comment Moderation",
    description:
      "Automatically filter spam, profanity, and toxic comments. Keep your community safe and welcoming.",
  },
  // COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation feature
  // {
  //   icon: MessageCircle,
  //   title: "Smart Auto-Replies",
  //   description:
  //     "Smart responses that match your brand voice. Answer FAQs instantly, 24/7.",
  // },
  {
    icon: BarChart3,
    title: "Operational Analytics",
    description:
      "Track engagement, response times, and community health with real-time dashboards.",
  },
  {
    icon: Languages,
    title: "Multi-Language Support",
    description:
      "Auto-detect and translate comments. Reply in your customer's language for truly global engagement.",
  },
  {
    icon: Brain,
    title: "Sentiment Analysis",
    description:
      "Smart system identifies urgent issues and prioritizes negative feedback. Never miss a critical customer concern.",
  },
  {
    icon: Users,
    title: "Team Collaboration",
    description:
      "Assign comments, add internal notes, and manage approval workflows across your team.",
  },
];

const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="features" className="py-24 bg-background relative">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-heading font-bold mb-4">
            Everything you need to{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              manage engagement
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Powerful features designed to save you time and protect your
            community
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group relative bg-card border border-border rounded-2xl p-8 hover:border-primary/50 hover:shadow-glow transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-8 h-8 text-primary-foreground" />
                </div>

                <h3 className="text-2xl font-heading font-bold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>

                {/* Hover gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
